// bluaIQ — Budget version supersede plan. PURE, no I/O.
//
// Closes the BUDGET half of the "three remaining Day-1 money write paths"
// (D-25AGO-09), against docs/SPEC_STATE_TRANSITIONS_MONEY_CLUSTER_V1.md §4.
//
// THE GAP THIS CLOSES. Budget was append-only: the only `prisma.budgetItem`
// calls in the whole server were `.create` and `.findMany`. There was no PATCH,
// no PUT and no DELETE, and the UI had one "Add Item" button and no per-row edit
// affordance. A typo in a budget line was permanent.
//
// WHY EDITING IS A NEW VERSION, NOT A PATCH (spec R-4.a/R-4.b). `Budget.version`
// already exists and was written-but-never-incremented. Editing a line in place
// would silently restate a number that a prior decision was made against, and
// would also mutate lines belonging to a version somebody has already read.
// Superseding keeps every prior version intact and readable, which is what makes
// the edit REVERSIBLE (spec §4.3 heading 2: hard-deleting a version is
// PROHIBITED). Every budget reader in the server already selects
// `orderBy: { version: 'desc' }`, so a new version is picked up everywhere
// without touching a single read site.
//
// NO LIFECYCLE STATE ON A LINE (spec §4.2). A budget item has no status and MUST
// NOT acquire one; its correctness is versioned at the Budget level. Nothing here
// stamps a status, a source, or an approval on a line — the source-trust table in
// BudgetModule is PRESENTATION of cross-module linkage and stays presentation.
//
// EXACT CENTS. Totals are summed in integer cents via the shared money helpers
// (parseMoneyToCents/sumCents), never with `+` over floats or Prisma Decimals.
// `Budget.totalCost` is DERIVED (spec R-4.c) — a version whose stored total
// disagrees with its own lines is a defect, so the total is computed HERE, from
// the exact line set being written, and never taken from the client.

import { parseMoneyToCents, sumCents, centsToMoneyString, isSafeCents } from '../shared/moneyPrecision'

/**
 * The DB-representable bound for the columns these values land in.
 * `Budget.totalCost` and `BudgetItem.amount` are `Decimal(14, 2)`: 14 significant
 * digits, 2 of them after the point, so the largest magnitude is
 * 999999999999.99 — i.e. 99999999999999 cents.
 *
 * A value beyond this is not "a big number", it is a value Postgres will REFUSE
 * at INSERT time. Catching it here turns a 500 from a numeric-overflow exception
 * into an explicit, per-line 422 that names the offending line, and it does so
 * BEFORE the transaction opens.
 */
export const DECIMAL_14_2_MAX_CENTS = 99_999_999_999_999

export function isWithinDecimal14x2(cents: number): boolean {
  return isSafeCents(cents) && Math.abs(cents) <= DECIMAL_14_2_MAX_CENTS
}

/** A line as it exists in the current (about to be superseded) version. */
export interface ExistingBudgetLine {
  id: string
  category: string
  label: string
  /** number | string | Prisma.Decimal — normalized through parseMoneyToCents. */
  amount: unknown
}

/** A line as the client asks for it in the NEW version. `sourceId`, when present,
 *  names the existing line this one continues — used ONLY to classify the delta
 *  for the audit payload and the confirmation UI. It is NOT persisted: inventing
 *  a provenance column is explicitly out of scope.
 *
 *  `amount` is the CANONICAL money value: a fixed two-decimal decimal string
 *  ("0.00", "12.35", "-600.25"). It is typed `unknown` because this module is
 *  the fail-closed boundary — it must reject whatever actually arrives rather
 *  than trust a type. A JS number is still accepted (the legacy initial-create
 *  route canonicalizes one), but the canonical STRING is what the new-version
 *  API carries, precisely so that no rounding can happen after the value the
 *  user confirmed. */
export interface RequestedBudgetLine {
  sourceId?: string
  category: string
  label: string
  amount: unknown
}

export interface PlannedBudgetLine {
  category: string
  label: string
  /** Exact integer cents. */
  cents: number
  /** Canonical decimal string for the Decimal(14,2) column — never a float. */
  amount: string
}

export interface BudgetVersionDelta {
  added: number
  changed: number
  removed: number
  unchanged: number
}

export type BudgetVersionPlan =
  | {
      ok: true
      lines: PlannedBudgetLine[]
      totalCents: number
      /** Canonical decimal string for Budget.totalCost. */
      totalAmount: string
      previousTotalCents: number
      previousTotalAmount: string
      delta: BudgetVersionDelta
    }
  | {
      ok: false
      reason:
        | 'empty-line-set' //     a version with no lines is a delete-all, not an edit
        | 'invalid-amount' //     a line amount is not exact money
        | 'invalid-line' //       blank category/label
        | 'unknown-source-line' //sourceId does not name a line of the current version
        | 'duplicate-source-line' // two requested lines claim the SAME existing line
        | 'unsafe-total' //       the summed total is not safe-integer cents
        | 'amount-out-of-db-range' // a line exceeds Decimal(14,2)
        | 'total-out-of-db-range' //  the derived total exceeds Decimal(14,2)
      /** 0-based index of the offending requested line, when the reason is
       *  per-line. Absent for whole-set reasons. */
      index?: number
    }

/** Normalize a stored amount (number | string | Decimal) to exact cents. Returns
 *  null when it cannot — a corrupt stored row must hard-fail, never silently
 *  become $0. */
function storedCents(amount: unknown): number | null {
  const raw = typeof amount === 'object' && amount !== null ? String(amount) : amount
  // Budget lines may legitimately be negative (a credit line), so negatives are
  // permitted here; the route's zod schema bounds what a CLIENT may send.
  const parsed = parseMoneyToCents(raw, { allowNegative: true, roundSubCent: false })
  return parsed.ok ? parsed.cents : null
}

/**
 * Compute the complete, validated line set + derived total for the NEXT budget
 * version, plus the delta the confirmation UI and the audit event report.
 *
 * FAIL-CLOSED: any line that does not parse to exact cents, any blank field, any
 * `sourceId` that does not name a line of the CURRENT version, and any duplicate
 * claim on the same existing line reject the whole plan. A partially-applied
 * budget version is not a state this product has.
 */
export function computeBudgetVersionPlan(
  existing: ReadonlyArray<ExistingBudgetLine>,
  requested: ReadonlyArray<RequestedBudgetLine>,
): BudgetVersionPlan {
  if (requested.length === 0) return { ok: false, reason: 'empty-line-set' }

  const existingById = new Map<string, ExistingBudgetLine>()
  for (const line of existing) existingById.set(line.id, line)

  const lines: PlannedBudgetLine[] = []
  const claimed = new Set<string>()
  let added = 0
  let changed = 0
  let unchanged = 0

  for (let i = 0; i < requested.length; i++) {
    const line = requested[i]
    const category = typeof line.category === 'string' ? line.category.trim() : ''
    const label = typeof line.label === 'string' ? line.label.trim() : ''
    if (!category || !label) return { ok: false, reason: 'invalid-line', index: i }

    const parsed = parseMoneyToCents(line.amount, { allowNegative: true, roundSubCent: false })
    if (!parsed.ok) return { ok: false, reason: 'invalid-amount', index: i }
    // A line Postgres cannot store is rejected HERE, with the offending index,
    // rather than as a numeric-overflow 500 from inside the transaction.
    if (!isWithinDecimal14x2(parsed.cents)) return { ok: false, reason: 'amount-out-of-db-range', index: i }

    if (line.sourceId !== undefined) {
      const source = existingById.get(line.sourceId)
      if (!source) return { ok: false, reason: 'unknown-source-line', index: i }
      if (claimed.has(line.sourceId)) return { ok: false, reason: 'duplicate-source-line', index: i }
      claimed.add(line.sourceId)
      const sourceCents = storedCents(source.amount)
      const identical =
        sourceCents !== null && sourceCents === parsed.cents && source.category === category && source.label === label
      if (identical) unchanged++
      else changed++
    } else {
      added++
    }

    lines.push({ category, label, cents: parsed.cents, amount: centsToMoneyString(parsed.cents) })
  }

  // Every existing line NOT claimed by a requested line is removed by this
  // version. Removal is by omission — and it is only a removal from the NEW
  // version: the prior version keeps the line, which is what makes it reversible.
  const removed = existing.length - claimed.size

  let totalCents: number
  let previousTotalCents: number
  try {
    totalCents = sumCents(lines.map((l) => l.cents))
    // A corrupt stored line contributes nothing to the PREVIOUS total rather than
    // throwing: the previous total is reported for the delta/audit only, and must
    // never be able to block a legitimate correction of that very budget.
    previousTotalCents = sumCents(existing.map((l) => storedCents(l.amount) ?? 0))
  } catch {
    return { ok: false, reason: 'unsafe-total' }
  }
  if (!isSafeCents(totalCents)) return { ok: false, reason: 'unsafe-total' }
  // The DERIVED total lands in Decimal(14,2) too. Individually-storable lines can
  // still sum past the column bound, so the total is checked on its own.
  if (!isWithinDecimal14x2(totalCents)) return { ok: false, reason: 'total-out-of-db-range' }

  return {
    ok: true,
    lines,
    totalCents,
    totalAmount: centsToMoneyString(totalCents),
    previousTotalCents,
    previousTotalAmount: centsToMoneyString(previousTotalCents),
    delta: { added, changed, removed, unchanged },
  }
}

/** Stable machine codes clients pin — never the English strings. */
export const BUDGET_VERSION_CODES = {
  BUDGET_NOT_FOUND: 'BUDGET_NOT_FOUND',
  BUDGET_VERSION_STALE: 'BUDGET_VERSION_STALE',
  INVALID_LINE_SET: 'INVALID_LINE_SET',
  // The legacy create route is INITIAL CREATION ONLY. Once a project has a
  // Budget, every further line change must go through a version supersede, or
  // the version guard is bypassable and edits can be silently lost.
  BUDGET_ALREADY_EXISTS: 'BUDGET_ALREADY_EXISTS',
  BUDGET_VERSION_UNKNOWN: 'BUDGET_VERSION_UNKNOWN',
} as const

/**
 * The CANONICAL money wire form for a budget line: a fixed two-decimal decimal
 * string. `"0.00"`, `"12.35"`, `"-600.25"`.
 *
 * Deliberately NOT a JS number. A number is lossy, and the shared adapter rounds
 * sub-cent numeric input by policy — which means, if a number were the wire
 * value, the server could round a value the user already confirmed and the four
 * quantities (shown → sent → accepted → persisted) could silently disagree. A
 * fixed two-decimal string cannot round: it either parses to exactly those cents
 * or it is refused.
 */
export const CANONICAL_MONEY_PATTERN = /^-?\d{1,12}\.\d{2}$/

export function isCanonicalMoneyString(value: unknown): value is string {
  return typeof value === 'string' && CANONICAL_MONEY_PATTERN.test(value)
}

export type CanonicalAmountResult =
  | { ok: true; amount: string; cents: number }
  | { ok: false; reason: 'missing' | 'not-money' | 'out-of-db-range' }

/**
 * Normalize raw user input (or a legacy numeric alias) into the canonical form,
 * ONCE, at the boundary where the user can still see the result.
 *
 * This is the single function the Budget editor uses to build BOTH the value it
 * shows in the confirmation delta AND the value it puts on the wire — which is
 * what makes "shown = sent = accepted = persisted" true rather than hoped for.
 * `roundSubCent` is ON here on purpose: it preserves the established V1 outcome
 * (12.345 → 12.35), and because it happens HERE the user sees 12.35 before
 * confirming. The server then re-parses that exact string with rounding OFF, so
 * no rounding can ever happen server-side.
 */
export function canonicalizeBudgetAmountInput(raw: unknown): CanonicalAmountResult {
  if (raw === null || raw === undefined || (typeof raw === 'string' && raw.trim() === '')) {
    return { ok: false, reason: 'missing' }
  }
  const parsed = parseMoneyToCents(typeof raw === 'string' ? raw.trim() : raw, {
    allowNegative: true,
    roundSubCent: true,
  })
  if (!parsed.ok) return { ok: false, reason: 'not-money' }
  if (!isWithinDecimal14x2(parsed.cents)) return { ok: false, reason: 'out-of-db-range' }
  return { ok: true, amount: centsToMoneyString(parsed.cents), cents: parsed.cents }
}
